import { useId, useState } from 'react';
import PropTypes from 'prop-types';

import { QUESTION_TYPES, QUESTION_TYPES_WITH_SUBQUESTIONS } from '@api/instant_survey';
import { getQuestionTypeIcon } from '@helpers';
import {
  ChevronDownIcon,
  ChevronRightOutlinedIcon,
  ExclamationTriangleIcon,
  IconButton,
  ResetIcon,
} from '@utilities';

import { isLanguageScreenerQuestion } from '../../../../../../utilities/helpers';
import TranslationRow from '../TranslationRow';
import { getTranslationWarnings } from '../../utilities/translationValidation';

import styles from '../../_index.module.scss';
import rowStyles from '../TranslationRow/_index.module.scss';

const QUESTION_TYPE_NAMES = Object.values(QUESTION_TYPES).reduce(
  (names, { name, value }) => ({ ...names, [value]: name }),
  {}
);

const getQuestionTypeName = (type) => (QUESTION_TYPE_NAMES[type] || type || '').toUpperCase();
const isInstructionQuestion = (question) => question?.type === QUESTION_TYPES.INSTRUCTIONS.value;
const isScreenerQuestion = (question) =>
  question?.isScreener || isLanguageScreenerQuestion(question);
const getQuestionDisplayLabel = (question, displayIndex) => {
  if (isInstructionQuestion(question)) return 'T';
  if (isScreenerQuestion(question)) return displayIndex;
  return `Q${displayIndex}`;
};

const isMaxDiffQuestion = (question) => question.type === QUESTION_TYPES.MAX_DIFF.value;

const getIssueCountForRow = ({
  appliedLanguages,
  entityKey,
  getEntry,
  getTranslation,
  sourceLanguage,
  sourceRichText,
  text,
}) =>
  appliedLanguages.reduce((count, language) => {
    const entry = getEntry?.(entityKey, language);
    const showRelevanceWarning = Boolean(
      entry && entry.isRelevant === false && entry.isUserUpdated === true
    );
    const validationWarnings = getTranslationWarnings({
      language: sourceLanguage,
      sourceRichText: sourceRichText || text,
      translationRichText: getTranslation(entityKey, language),
    });
    return count + validationWarnings.length + (showRelevanceWarning ? 1 : 0);
  }, 0);

const QuestionTranslations = ({
  appliedLanguages,
  displayIndex,
  getEntry,
  getTranslation,
  hasConceptName,
  isDisabled,
  languageLabels,
  onClear,
  onDismissRelevanceWarning,
  onPrioritizeQuestion,
  onReset,
  onTranslationChange,
  pendingTranslationKeys,
  question,
  registerQuestion,
  sourceLanguage,
}) => {
  const contentId = useId();
  const toggleId = `${contentId}-toggle`;
  const [isExpanded, setIsExpanded] = useState(false);
  const answerSourceIcon = getQuestionTypeIcon(question.type);
  const questionDisplayLabel = getQuestionDisplayLabel(question, displayIndex);
  const questionText = question.text || '';
  const expandedHeaderText = `${questionDisplayLabel} · ${getQuestionTypeName(question.type)}`;
  const collapsedHeaderText = questionText
    ? `${questionDisplayLabel} · ${questionText}`
    : questionDisplayLabel;
  const toggleLabel = isExpanded ? expandedHeaderText : collapsedHeaderText;
  const questionTextIssueCount = getIssueCountForRow({
    appliedLanguages,
    entityKey: `question-${question.id}`,
    getEntry,
    getTranslation,
    sourceLanguage,
    sourceRichText: question.richText || question.text,
    text: question.text,
  });
  const bestLabelIssueCount =
    isMaxDiffQuestion(question) && question.properties?.bestLabel
      ? getIssueCountForRow({
          appliedLanguages,
          entityKey: `maxdiff-best-label-${question.id}`,
          getEntry,
          getTranslation,
          sourceLanguage,
          sourceRichText: question.properties.bestLabel,
          text: `Best label: ${question.properties.bestLabel}`,
        })
      : 0;
  const worstLabelIssueCount =
    isMaxDiffQuestion(question) && question.properties?.worstLabel
      ? getIssueCountForRow({
          appliedLanguages,
          entityKey: `maxdiff-worst-label-${question.id}`,
          getEntry,
          getTranslation,
          sourceLanguage,
          sourceRichText: question.properties.worstLabel,
          text: `Worst label: ${question.properties.worstLabel}`,
        })
      : 0;
  const answerIssueCount = isLanguageScreenerQuestion(question)
    ? 0
    : (question.answers || [])
        .filter((answer) => !answer._delete)
        .reduce(
          (count, answer) =>
            count +
            getIssueCountForRow({
              appliedLanguages,
              entityKey: `option-${question.id}-${answer.id}`,
              getEntry,
              getTranslation,
              sourceLanguage,
              sourceRichText: answer.richText || answer.text,
              text: answer.text,
            }),
          0
        );
  const subQuestionIssueCount = QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type)
    ? (question.subQuestions || [])
        .filter((subQuestion) => !subQuestion._delete)
        .reduce(
          (count, subQuestion) =>
            count +
            getIssueCountForRow({
              appliedLanguages,
              entityKey: `subquestion-${question.id}-${subQuestion.id}`,
              getEntry,
              getTranslation,
              sourceLanguage,
              sourceRichText: subQuestion.richText || subQuestion.text,
              text: subQuestion.text,
            }),
          0
        )
    : 0;
  const questionIssueCount =
    questionTextIssueCount +
    bestLabelIssueCount +
    worstLabelIssueCount +
    answerIssueCount +
    subQuestionIssueCount;

  return (
    <div
      className={styles['modal-translation-section']}
      ref={registerQuestion ? registerQuestion(question.id) : undefined}
    >
      <div className={styles['modal-translation-section-header']}>
        <h3 aria-label={toggleLabel}>
          <button
            aria-controls={contentId}
            aria-expanded={isExpanded}
            aria-label={toggleLabel}
            className={styles['modal-translation-section-toggle']}
            id={toggleId}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            type="button"
          >
            <span className={styles['modal-translation-section-chevron']}>
              {isExpanded ? <ChevronDownIcon /> : <ChevronRightOutlinedIcon />}
            </span>
            <span className={styles['modal-translation-section-summary']}>
              <span className={styles['modal-translation-section-title']}>
                {isExpanded ? expandedHeaderText : questionDisplayLabel}
              </span>
              {!isExpanded && questionText && (
                <span className={styles['modal-translation-section-question-text']}>
                  {`· ${questionText}`}
                </span>
              )}
            </span>
            {questionIssueCount > 0 && (
              <span
                aria-hidden="true"
                className={styles['modal-translation-section-issue-indicator']}
                data-testid={`question-issue-indicator-${question.id}`}
                title={`${questionIssueCount} translation issue${
                  questionIssueCount === 1 ? '' : 's'
                }`}
              >
                <ExclamationTriangleIcon />
                <span>{questionIssueCount}</span>
              </span>
            )}
          </button>
        </h3>
        {appliedLanguages.length > 0 && !isLanguageScreenerQuestion(question) && (
          <IconButton
            aria-label={`Reset ${questionDisplayLabel} translations`}
            className={styles['modal-translation-reset']}
            isDisabled={isDisabled}
            onClick={() => onReset(question.id)}
            tooltip="Reset"
            type="button"
          >
            <ResetIcon />
          </IconButton>
        )}
      </div>
      <div aria-labelledby={toggleId} hidden={!isExpanded} id={contentId} role="region">
        <TranslationRow
          appliedLanguages={appliedLanguages}
          entityKey={`question-${question.id}`}
          getEntry={getEntry}
          getTranslation={getTranslation}
          hasConceptName={hasConceptName}
          isDisabled={isDisabled || isLanguageScreenerQuestion(question)}
          languageLabels={languageLabels}
          onClear={() => onClear(`question-${question.id}`)}
          onDismissRelevanceWarning={onDismissRelevanceWarning}
          onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
          onTranslationChange={onTranslationChange}
          pendingTranslationKeys={pendingTranslationKeys}
          sourceLanguage={sourceLanguage}
          sourceRichText={question.richText || question.text}
          text={question.text}
        />
        {isMaxDiffQuestion(question) && question.properties?.bestLabel && (
          <TranslationRow
            appliedLanguages={appliedLanguages}
            entityKey={`maxdiff-best-label-${question.id}`}
            getEntry={getEntry}
            getTranslation={getTranslation}
            hasConceptName={hasConceptName}
            isDisabled={isDisabled}
            languageLabels={languageLabels}
            onClear={() => onClear(`maxdiff-best-label-${question.id}`)}
            onDismissRelevanceWarning={onDismissRelevanceWarning}
            onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
            onTranslationChange={onTranslationChange}
            pendingTranslationKeys={pendingTranslationKeys}
            sourceLanguage={sourceLanguage}
            sourceRichText={question.properties.bestLabel}
            text={`Best label: ${question.properties.bestLabel}`}
          />
        )}
        {isMaxDiffQuestion(question) && question.properties?.worstLabel && (
          <TranslationRow
            appliedLanguages={appliedLanguages}
            entityKey={`maxdiff-worst-label-${question.id}`}
            getEntry={getEntry}
            getTranslation={getTranslation}
            hasConceptName={hasConceptName}
            isDisabled={isDisabled}
            languageLabels={languageLabels}
            onClear={() => onClear(`maxdiff-worst-label-${question.id}`)}
            onDismissRelevanceWarning={onDismissRelevanceWarning}
            onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
            onTranslationChange={onTranslationChange}
            pendingTranslationKeys={pendingTranslationKeys}
            sourceLanguage={sourceLanguage}
            sourceRichText={question.properties.worstLabel}
            text={`Worst label: ${question.properties.worstLabel}`}
          />
        )}
        {isLanguageScreenerQuestion(question)
          ? question.answers
              ?.filter((answer) => !answer._delete)
              .map((answer) => (
                <div
                  className={`${rowStyles['modal-translation-row']} ${rowStyles['modal-translation-row-readonly']}`}
                  key={answer.id}
                >
                  <span className={rowStyles['modal-translation-row-source']}>{answer.text}</span>
                </div>
              ))
          : question.answers
              ?.filter((answer) => !answer._delete)
              .map((answer) => (
                <TranslationRow
                  appliedLanguages={appliedLanguages}
                  entityKey={`option-${question.id}-${answer.id}`}
                  getEntry={getEntry}
                  getTranslation={getTranslation}
                  hasConceptName={hasConceptName}
                  isDisabled={isDisabled}
                  key={answer.id}
                  languageLabels={languageLabels}
                  onClear={() => onClear(`option-${question.id}-${answer.id}`)}
                  onDismissRelevanceWarning={onDismissRelevanceWarning}
                  onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
                  onTranslationChange={onTranslationChange}
                  pendingTranslationKeys={pendingTranslationKeys}
                  sourceIcon={answerSourceIcon}
                  sourceLanguage={sourceLanguage}
                  sourceRichText={answer.richText || answer.text}
                  text={answer.text}
                />
              ))}
        {QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type) &&
          question.subQuestions
            ?.filter((subQuestion) => !subQuestion._delete)
            .map((subQuestion) => (
              <TranslationRow
                appliedLanguages={appliedLanguages}
                entityKey={`subquestion-${question.id}-${subQuestion.id}`}
                getEntry={getEntry}
                getTranslation={getTranslation}
                hasConceptName={hasConceptName}
                isDisabled={isDisabled}
                key={subQuestion.id}
                languageLabels={languageLabels}
                onClear={() => onClear(`subquestion-${question.id}-${subQuestion.id}`)}
                onDismissRelevanceWarning={onDismissRelevanceWarning}
                onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
                onTranslationChange={onTranslationChange}
                pendingTranslationKeys={pendingTranslationKeys}
                sourceLanguage={sourceLanguage}
                sourceRichText={subQuestion.richText || subQuestion.text}
                text={subQuestion.text}
              />
            ))}
      </div>
    </div>
  );
};

QuestionTranslations.propTypes = {
  appliedLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  displayIndex: PropTypes.string.isRequired,
  getEntry: PropTypes.func.isRequired,
  getTranslation: PropTypes.func.isRequired,
  hasConceptName: PropTypes.bool,
  isDisabled: PropTypes.bool,
  languageLabels: PropTypes.object.isRequired,
  onClear: PropTypes.func.isRequired,
  onDismissRelevanceWarning: PropTypes.func.isRequired,
  onPrioritizeQuestion: PropTypes.func,
  onReset: PropTypes.func.isRequired,
  onTranslationChange: PropTypes.func.isRequired,
  pendingTranslationKeys: PropTypes.instanceOf(Set),
  question: PropTypes.object.isRequired,
  registerQuestion: PropTypes.func,
  sourceLanguage: PropTypes.string,
};

export default QuestionTranslations;
